<template>
    <div
        class="asset-allocation-chart"
        ref="assetAllocationChart" />
</template>

<script>
import * as d3 from 'd3';
import { mapActions, mapState } from 'vuex';

import { DetailsSubFilterType } from '@/api/enums';
import { isIE } from '@/components/utils/compatibility';

export default {
    name: 'ModelPortfolioAssetAllocationChart',
    computed: {
        ...mapState(['modelPortfolioDetails', 'selectedDetailsSubFilterType']),
        chartCursor () {
            return !isIE() ? 'pointer' : '';
        },
    },
    data () {
        return {
            chart: null,
            icons: Object.keys(DetailsSubFilterType)
                .reduce((map, obj) => {
                    map[obj] = require(`@/components/common/icons/details-${obj.toLowerCase().replace('_', '-')}.svg`);
                    return map;
                }, {}),
            config: {
                sideLength: 250,
                margin: 22,
                innerRadius: 55,
                highlightRadiusModifier: 1.1,
                animationGrowRadiusModifier: 1.07,
                animationDuration: 500,
                iconSize: 24,
                iconColor: 'white',
                outerRadius () { return this.sideLength / 2 - this.margin; },
                canShowIcon (value) { return value > 6; },
            },
        };
    },
    watch: {
        modelPortfolioDetails (val) {
            this.renderChart(val);
        },
        selectedDetailsSubFilterType (val) {
            if (this.modelPortfolioDetails) {
                this.renderChart(this.modelPortfolioDetails);
            }
        },
    },
    methods: {
        ...mapActions(['setSelectedDetailsSubFilterType']),
        createChartSvg () {
            if (this.chart !== null) {
                this.chart.remove();
            };

            const config = this.config;

            this.chart = d3.select(this.$refs.assetAllocationChart)
                .append('svg')
                .attr('width', config.sideLength)
                .attr('height', config.sideLength);
            this.chart.append('defs');
        },
        onAssetDetailsClick (assetType) {
            if (!isIE()) {
                this.setSelectedDetailsSubFilterType(assetType);
            }
        },
        renderArcs (data) {
            const config = this.config;
            const transformCenter = config.sideLength / 2;

            const svg = this.chart
                .append('g')
                .attr('transform', `translate(${transformCenter}, ${transformCenter})`);

            // calculate the position of each group on the chart
            const pie = d3.pie()
                .sort(null)
                .value(d => d.value.percentage);
            const pieData = pie(d3.entries(data));

            const arc = d3.arc()
                .innerRadius(d => config.innerRadius)
                .outerRadius(d => config.outerRadius());

            // build the chart
            svg
                .selectAll('.pie')
                .data(pieData)
                .enter()
                .append('g')
                .on('click', d => this.onAssetDetailsClick(d.data.value.detailsSubFilterType))
                .attr('cursor', this.chartCursor)
                .attr('id', d => `pie-arc-${d.data.value.detailsSubFilterType}`)
                .attr('class', (d, i) => `pie-arc details-sub-filter-color--${i}`)
                .append('path')
                .attr('d', d => {
                    d.innerRadius = arc.innerRadius()(d);
                    d.outerRadius = arc.outerRadius()(d);
                    return arc(d);
                })
                .attr('filter', 'url(#dropshadow)');

            return {
                svg,
                arcCenters: pieData.map(arc.centroid),
            };
        },
        highlightSelectedArc (svg) {
            const config = this.config;

            const selectedArc = svg.select(`#pie-arc-${this.selectedDetailsSubFilterType}`);
            if (selectedArc.empty()) {
                return;
            }

            const selectedArcData = selectedArc.datum();

            const arcHighlight = d3.arc()
                .innerRadius(config.innerRadius * config.highlightRadiusModifier)
                .outerRadius(config.outerRadius() * config.highlightRadiusModifier)
                .startAngle(selectedArcData.startAngle)
                .endAngle(selectedArcData.endAngle);

            selectedArc.append('path')
                .lower()
                .attr('class', 'pie-arc-highlight details-sub-filter-color--selected')
                .attr('d', arcHighlight);

            // animate
            selectedArc
                .transition()
                .duration(500)
                .attr('transform', `scale(${config.animationGrowRadiusModifier})`);
        },
        fetchIcons (data) {
            const config = this.config;
            const icons = this.icons;

            data.forEach((d, i) => {
                if (config.canShowIcon(d.percentage)) {
                    d3.xml(icons[d.detailsSubFilterType])
                        .then(xmlIconData => {
                            this.chart.select('defs')
                                .append('svg')
                                .attr('id', `pie-icon-def-${d.detailsSubFilterType}`)
                                .node()
                                .append(xmlIconData.documentElement);

                            // icon size update
                            this.chart.select(`#pie-icon-def-${d.detailsSubFilterType} > svg`)
                                .attr('class', 'pie-icon-svg')
                                .attr('width', config.iconSize)
                                .attr('height', config.iconSize)
                                .selectAll('path')
                                .attr('stroke', config.iconColor) // make the color stick for FF
                                .attr('class', 'pie-icon-svg-path');
                        });
                }
            });
        },
        renderIcons (svg, arcCenters) {
            const config = this.config;

            // place icons on the chart
            svg
                .selectAll('.pie-arc')
                .append('use')
                .attr('class', 'pie-icon')
                .attr('x', (d, i) => arcCenters[i][0] - config.iconSize / 2)
                .attr('y', (d, i) => arcCenters[i][1] - config.iconSize / 2)
                .attr('href', (d, i) => `#pie-icon-def-${d.data.value.detailsSubFilterType}`);
        },
        defineDropshadow () {
            const filter = this.chart.select('defs')
                .append('filter')
                .attr('id', 'dropshadow');

            filter.append('feGaussianBlur')
                .attr('in', 'SourceAlpha')
                .attr('stdDeviation', 2)
                .attr('result', 'blur');
            filter.append('feOffset')
                .attr('in', 'blur')
                .attr('dx', 0)
                .attr('dy', 4)
                .attr('result', 'offsetBlur');
            filter.append('feFlood')
                .attr('in', 'offsetBlur')
                .attr('flood-color', 'gray')
                .attr('flood-opacity', '0.5')
                .attr('result', 'offsetColor');
            filter.append('feComposite')
                .attr('in', 'offsetColor')
                .attr('in2', 'offsetBlur')
                .attr('operator', 'in')
                .attr('result', 'offsetBlur');

            const feMerge = filter.append('feMerge');

            feMerge.append('feMergeNode')
                .attr('in', 'offsetBlur');
            feMerge.append('feMergeNode')
                .attr('in', 'SourceGraphic');
        },
        renderChart (data) {
            this.createChartSvg();
            this.defineDropshadow();

            const { svg, arcCenters } = this.renderArcs(data);
            this.fetchIcons(data);
            this.renderIcons(svg, arcCenters);
            this.highlightSelectedArc(svg);
        },
    },
    mounted () {
        if (this.modelPortfolioDetails) {
            this.renderChart(this.modelPortfolioDetails);
        }
    },
};
</script>

<style scoped lang="scss">
.asset-allocation-chart {
  margin-top: -15px;
  text-align: center;

  @include media-breakpoint-down(xs) {
    margin: -20px auto 18px auto;
  }

  ::v-deep .details-sub-filter-color--selected {
    fill: #e9efdc;
  }
}
</style>
